import React from "react";
import { Box, SpaceBetween } from "@cloudscape-design/components";

const REPORT_HELP_SECTIONS = [
  {
    title: "How funding is assigned to a period",
    paragraphs: [
      "Funding is counted on its scheduled payment date. For recurring payments, each instalment counts in the period when it is due. If an intervention spans several periods, only the amounts due in the selected period are included. Approval and data-entry dates do not determine the reporting period.",
      "The fiscal year runs from April 1 to March 31. Q1 is April–June, Q2 July–September, Q3 October–December and Q4 January–March of the following calendar year.",
      "If a payment schedule is missing or cannot be used, PATH may use a recorded due date or the intervention start date. The warning on that record explains how its funding was counted and what needs checking.",
    ],
  },
  {
    title: "How the province or territory is determined",
    paragraphs: [
      "The region comes from the budget pot that funds the intervention. Changing a client’s address does not move their funding to another region. For example, funding assigned to a Saskatchewan pot stays in Saskatchewan if the client moves to Alberta.",
      "The report uses the current funding assignment. Moving an intervention to a different regional pot, or correcting the pot’s region, can change regional totals. Funding with no clear region appears under Unassigned pot and is flagged for review.",
    ],
  },
  {
    title: "What funding warnings mean",
    paragraphs: [
      "Some funding details are missing or do not match. Each warning explains what needs checking and how the report has counted the funding. Flagged funding may still be included in the totals.",
      "These warnings typically arise in manually entered historical (pre-PATH) interventions, where funding or approval records may be incomplete. They can also appear on newer records.",
      "Back-loaded action plans and interventions do not need a PATH application. Having no application link is expected and is not an error.",
      "Common causes include missing payment dates or cost details, amounts that do not add up, negative funding amounts, or an unclear CRF/EI source or region. For an intervention created from a PATH application, a lost or conflicting application link can also need review. Funding that remains active after its application was denied or withdrawn may need checking.",
      "Read the warning before changing a record. It explains any amount included or excluded and any substitute date or funding assignment used. Use Open intervention or Open case to review the record. Searching the detail table does not hide these warnings.",
      "Approved reimbursements included is informational. It explains funding that was separately approved after an application was declined. No correction is needed for that reason alone.",
    ],
  },
  {
    title: "Why totals can change",
    paragraphs: [
      "This report shows scheduled funding. It does not confirm that payments have been made. Changes to approved amounts, cancellations and payment schedules can change totals for earlier periods.",
      "The report filters and the search in Intervention detail apply to both summary totals and the Excel export. Export includes all matching rows, including those on other pages.",
    ],
  },
];

const FinanceReportsHelp = () => (
  <SpaceBetween size="m">
    {REPORT_HELP_SECTIONS.map(({ title, paragraphs }) => (
      <Box key={title}>
        <strong>{title}</strong>
        {paragraphs.map(text => <p key={text}>{text}</p>)}
      </Box>
    ))}
  </SpaceBetween>
);

const HelpParagraphs = ({ paragraphs }) => (
  <SpaceBetween size="s">
    {paragraphs.map(text => <Box key={text}>{text}</Box>)}
  </SpaceBetween>
);

const SETUP_HELP = [
  "This report shows approved CRF and EI funding scheduled for the selected period. Each instalment is counted on its scheduled payment date. The amounts do not confirm that payments have been made.",
  "Region follows the budget pot that funds the intervention. Changing a client’s address does not move their funding to another region.",
  "Choose Funded interventions only to show funding above $0. All reportable interventions also includes interventions with no funding scheduled in the selected period.",
];

export const FinanceReportsSetupHelp = () => <HelpParagraphs paragraphs={SETUP_HELP} />;
FinanceReportsSetupHelp.aiContext = SETUP_HELP.join("\n\n");

const SUMMARY_HELP = [
  "Total funding is CRF plus EI funding scheduled in the selected period. The report filters and the search in Intervention detail apply to these totals.",
  "A funded client has funding above $0 in the selected period. Each person is counted once, even if they have several interventions.",
  "Each intervention is counted once. All reportable interventions also counts interventions with $0 scheduled.",
];

export const FinanceReportsSummaryHelp = () => <HelpParagraphs paragraphs={SUMMARY_HELP} />;
FinanceReportsSummaryHelp.aiContext = SUMMARY_HELP.join("\n\n");

const REGION_HELP = [
  "Funding is grouped by the region of the budget pot that funds the intervention. A client’s current address does not determine the region.",
  "A client with funding in more than one region is counted once in each of those regions, but only once in the overall Funded clients total. Adding the regional client counts may therefore give a higher number.",
  "Unassigned pot shows funding whose region needs checking. Review the warning for that record.",
];

export const FinanceReportsRegionSummaryHelp = () => <HelpParagraphs paragraphs={REGION_HELP} />;
FinanceReportsRegionSummaryHelp.aiContext = REGION_HELP.join("\n\n");

const DETAIL_HELP = [
  "The Funding column shows the recorded funding source. The cost columns show the amounts for tuition, living costs and other support.",
  "The funding and cost amounts cover only scheduled payments in the selected period. They may be less than the intervention’s full approved amount.",
  "Searching this table also changes the summary totals. Export to Excel includes all matching rows, including those on other pages.",
];

export const FinanceReportsDetailHelp = () => <HelpParagraphs paragraphs={DETAIL_HELP} />;
FinanceReportsDetailHelp.aiContext = DETAIL_HELP.join("\n\n");

FinanceReportsHelp.aiContext = [
  ...REPORT_HELP_SECTIONS.map(({ title, paragraphs }) => [title, ...paragraphs].join("\n")),
  ["ISET Advances and Active Clients", ...SETUP_HELP].join("\n"),
  ["Report summary", ...SUMMARY_HELP].join("\n"),
  ["Region summary", ...REGION_HELP].join("\n"),
  ["Intervention detail", ...DETAIL_HELP].join("\n"),
].join("\n\n");

export default FinanceReportsHelp;
